import { ALGOLIA } from './constants';
import { haversineMiles } from './geo';

export function hasAlgolia() {
  return Boolean(ALGOLIA.appId && ALGOLIA.searchKey);
}

function mapHit(hit) {
  return {
    id: hit.objectID,
    title: hit.title || '',
    description: hit.description || '',
    price: hit.price ?? 0,
    isFree: hit.isFree === true,
    currency: hit.currency || 'USD',
    category: hit.category || '',
    condition: hit.condition || '',
    photos: hit.photos || (hit.mainPhoto ? [hit.mainPhoto] : []),
    mainPhotoIndex: hit.mainPhotoIndex ?? 0,
    quantity: hit.quantity ?? 1,
    sellerId: hit.sellerId || '',
    status: hit.status || 'approved',
    supportsShipping: Boolean(hit.supportsShipping),
    previousPrice: hit.previousPrice ?? null,
    saveCount: hit.saveCount ?? 0,
    likeCount: hit.likeCount ?? 0,
    viewCount: hit.viewCount ?? 0,
    createdAtMs: hit.createdAtMs || hit.createdAt || 0,
    contentUpdatedAtMs: hit.contentUpdatedAtMs || 0,
    priceDroppedAtMs: hit.priceDroppedAtMs || 0,
    pickup: {
      label: hit.pickupLabel || hit.pickup?.label || '',
      city: hit.pickup?.city || '',
      state: hit.pickup?.state || '',
      lat: hit._geoloc?.lat ?? hit.pickup?.lat ?? 0,
      lng: hit._geoloc?.lng ?? hit.pickup?.lng ?? 0,
      geohash: hit.geohash || '',
      pickupMethod: hit.pickup?.pickupMethod || '',
    },
  };
}

export async function searchListings({
  query = '',
  category = 'All',
  lat,
  lng,
  radiusMiles = 50,
  sort = 'nearest',
  page = 0,
  hitsPerPage = 60,
} = {}) {
  if (!hasAlgolia()) return null;

  const filters = ['status:approved'];
  if (category && category !== 'All') {
    filters.push(`category:"${category.replace(/"/g, '')}"`);
  }

  const body = {
    query: query.trim(),
    filters: filters.join(' AND '),
    hitsPerPage,
    page,
  };

  if (typeof lat === 'number' && typeof lng === 'number') {
    body.aroundLatLng = `${lat},${lng}`;
    body.aroundRadius = Math.round(radiusMiles * 1609.34);
  }

  if (sort === 'price_asc') body.numericFilters = [];
  // Algolia replica indexes aren't configured here; we sort client-side after fetch.

  const res = await fetch(
    `https://${ALGOLIA.appId}-dsn.algolia.net/1/indexes/${ALGOLIA.index}/query`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Algolia-Application-Id': ALGOLIA.appId,
        'X-Algolia-API-Key': ALGOLIA.searchKey,
      },
      body: JSON.stringify(body),
    },
  );

  if (!res.ok) throw new Error('Search failed');
  const data = await res.json();
  let listings = (data.hits || []).map(mapHit);

  listings = sortListings(listings, sort, lat, lng);
  return listings;
}

export function sortListings(listings, sort, lat, lng) {
  const withDist = listings.map((item) => {
    const p = item.pickup;
    const miles =
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      p?.lat &&
      p?.lng
        ? haversineMiles(lat, lng, p.lat, p.lng)
        : null;
    return { ...item, _distance: miles };
  });

  const copy = [...withDist];
  switch (sort) {
    case 'price_asc':
      copy.sort((a, b) => (a.price || 0) - (b.price || 0));
      break;
    case 'price_desc':
      copy.sort((a, b) => (b.price || 0) - (a.price || 0));
      break;
    case 'newest':
      copy.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
      break;
    case 'nearest':
    default:
      copy.sort((a, b) => {
        if (a._distance == null && b._distance == null) return 0;
        if (a._distance == null) return 1;
        if (b._distance == null) return -1;
        return a._distance - b._distance;
      });
      break;
  }
  return copy;
}

export function filterByRadius(listings, lat, lng, radiusMiles) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return listings;
  return listings.filter((item) => {
    const p = item.pickup;
    if (!p?.lat || !p?.lng) return true;
    return haversineMiles(lat, lng, p.lat, p.lng) <= radiusMiles;
  });
}
